// Backend configuration with dynamic port discovery for local development.
//
// In development: reads port from .backend-port file (written by backend startup script)
// In production: uses VITE_BACKEND_API_URL environment variable
//
// TRAC-450: Non-prod environments support a dev-only "region pin" that
// rewrites the API hostname to a region-pinned variant (e.g.
// api-dev.traci.mi.tirerack.com -> api-dev-useast1.traci.mi.tirerack.com).
// Used by the ExperimentBadge region selector so devs can test each
// region manually without waiting for Global Accelerator to route them.

import { BACKEND_URL as DISCOVERED_URL } from "virtual:backend-config";

const REGION_PIN_KEY = "traci-api-region-override";
const REGION_PIN_TIMESTAMP_KEY = "traci-api-region-override-timestamp";

export type RegionPin = "auto" | "us-east-1" | "us-west-2";

/** Read the current region pin from localStorage. Safe on SSR. */
export function getRegionPin(): RegionPin {
  if (typeof window === "undefined") return "auto";
  try {
    const v = localStorage.getItem(REGION_PIN_KEY);
    if (v === "us-east-1" || v === "us-west-2") return v;
    return "auto";
  } catch {
    return "auto";
  }
}

/**
 * Persist the region pin. Reloads the page so any cached/in-flight requests
 * also restart against the new pin.
 *
 * TRAC-550 B2: getBackendApiUrl() now re-reads the pin on every call, so the
 * reload is no longer required for *new* calls to land on the right region.
 * It's kept here for the manual ExperimentBadge dev tool, which expects a
 * full reset. Pin-flip during runtime (B4-B5) bypasses this entry point.
 */
export function setRegionPin(val: RegionPin): void {
  persistRegionPin(val);
  if (typeof window !== "undefined") window.location.reload();
}

/**
 * Persist the region pin without reloading. Used by TRAC-550 B4's pin-flip
 * state machine — getBackendApiUrl() re-reads the pin on every call so the
 * next request automatically lands on the new region. Also writes the pin
 * timestamp so B4's TTL check can revert stale pins on app load.
 */
export function persistRegionPin(val: RegionPin): void {
  try {
    if (val === "auto") {
      localStorage.removeItem(REGION_PIN_KEY);
      localStorage.removeItem(REGION_PIN_TIMESTAMP_KEY);
    } else {
      localStorage.setItem(REGION_PIN_KEY, val);
      localStorage.setItem(REGION_PIN_TIMESTAMP_KEY, Date.now().toString());
    }
  } catch {
    // no-op; localStorage can be blocked in some browsers
  }
}

/** Read the timestamp the current pin was last written, or null if not set / unparseable. */
export function getRegionPinTimestamp(): Date | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(REGION_PIN_TIMESTAMP_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? new Date(n) : null;
  } catch {
    return null;
  }
}

/**
 * Transform api-{env}.traci.mi.tirerack.com -> api-{env}-{regionSuffix}.traci.mi.tirerack.com
 * No-op if the input doesn't match the expected shape.
 */
function applyRegionPin(base: string, pin: RegionPin): string {
  if (pin === "auto") return base;
  const suffix = pin === "us-east-1" ? "useast1" : "uswest2";
  // Match api-<env>. where <env> is one token (no dashes) — avoids re-pinning
  // an already-pinned URL like api-dev-useast1.
  return base.replace(
    /^(https?:\/\/api-[a-z0-9]+)\.(traci\.[^/]+)/,
    `$1-${suffix}.$2`,
  );
}

/**
 * TRAC-907: how long a region pin stays honored in prod. Deliberately much
 * shorter than regionFailoverService's 60-minute PIN_TTL_MS. That TTL governs
 * a *failover* pin the app set itself for a good reason; this one governs an
 * escape hatch that any visitor can trigger by appending `?health_check=true`,
 * so it should expire fast. The canary's whole flip happens across two chat
 * turns inside a single 5-minute run, so 15 minutes is generous.
 *
 * Declared here rather than imported from regionFailoverService because that
 * module imports from this one — importing back would be circular.
 */
const PROD_PIN_TTL_MS = 15 * 60 * 1000;

function readHealthCheckMarker(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get("health_check") === "true"
    );
  } catch {
    return false;
  }
}

/**
 * Is this page load the synthetics health check? Captured once at module load
 * rather than re-read per call. The canary reloads the page mid-flip, which
 * re-runs this with the marker still on the URL; capturing once means later
 * client-side navigation can't silently drop the param and strand the canary
 * on an unpinned URL between its two turns.
 */
const IS_HEALTH_CHECK = readHealthCheckMarker();

/**
 * TRAC-1002: is this page load the synthetics continuity canary? The chat uses
 * it to let the canary past the maintenance screen, so the canary keeps sending
 * real turns through an outage and `chat-turns-failing` can see them. Same
 * captured-once marker as the region pin above, for the same reason. Any
 * visitor can set the flag; for them it only means skipping the screen and
 * seeing the errors instead.
 */
export function isSyntheticHealthCheck(): boolean {
  return IS_HEALTH_CHECK;
}

/**
 * Whether prod should honor a region pin at all. Real customer traffic must
 * never be pinned: a pinned customer stops benefiting from Global
 * Accelerator's failover and would ride a degraded region down. The one
 * exception is the synthetics continuity canary, which can only verify that a
 * session survives a cross-region flip by actually performing one.
 *
 * Both conditions are required, and the TTL is re-checked on every call so a
 * stale pin left in localStorage stops being honored on its own.
 */
function prodPinIsHonored(): boolean {
  if (!IS_HEALTH_CHECK) return false;
  const pinnedAt = getRegionPinTimestamp();
  if (pinnedAt === null) return false;
  return Date.now() - pinnedAt.getTime() < PROD_PIN_TTL_MS;
}

function computeBackendApiUrl(): string {
  // Local dev: use the vite-dev-server-discovered URL, no pinning.
  if (import.meta.env.DEV) return DISCOVERED_URL;

  const base =
    import.meta.env.VITE_BACKEND_API_URL || "http://localhost:8081";
  const env = (import.meta.env.VITE_ENV as string | undefined) || "";

  // Prod must never be pinned by customer traffic — gate the override here too
  // (belt + suspenders with the ExperimentBadge's env gate). TRAC-907 carves
  // out the synthetics canary: without it the prod continuity canary fails
  // 100% of runs at the region-symmetry check and its alarm sits permanently
  // red, which looks like coverage while reporting nothing.
  if (env === "prod" && !prodPinIsHonored()) return base;

  return applyRegionPin(base, getRegionPin());
}

/**
 * Returns the current backend API URL, re-reading the region pin on every
 * call. Use this everywhere a request is about to be made — never cache the
 * return value at module load or inside a closure that outlives a single
 * request. (TRAC-550 B2: replaces the former module-load BACKEND_API_URL
 * constant so pin-flip can take effect at runtime without a page reload.)
 */
export function getBackendApiUrl(): string {
  return computeBackendApiUrl();
}

/**
 * Returns the API URL pinned to a specific region, ignoring the user's
 * current region pin. Used by TRAC-550 B3's region-health hook to poll
 * BOTH regions independently for failover detection.
 *
 * In local dev (`import.meta.env.DEV`) returns the discovered local URL
 * unchanged — there's no per-region split locally, so both regions resolve
 * to the same backend.
 */
export function getRegionApiUrl(region: "us-east-1" | "us-west-2"): string {
  if (import.meta.env.DEV) return DISCOVERED_URL;
  const base =
    import.meta.env.VITE_BACKEND_API_URL || "http://localhost:8081";
  return applyRegionPin(base, region);
}

/**
 * Primary-region API URL — always points at us-east-1, ignoring the region
 * pin. Use this for calls that depend on resources that only exist in
 * us-east-1: Langfuse traces (ClickHouse at clickhouse.langfuse.local), the
 * Langfuse UI/API itself, and anything else that documents
 * "us-east-1 only" in CLAUDE.md's multi-region table.
 *
 * Why a separate constant instead of bypassing the pin inline: the admin
 * analytics pages query ClickHouse, which lives in the us-east-1 VPC only.
 * If a dev pins the frontend to us-west-2 to test chat flows, we still
 * want the dashboards to work — they should silently keep going through
 * us-east-1 for those Langfuse-backed queries.
 */
function computePrimaryRegionApiUrl(): string {
  // Local dev: use the same discovered URL as normal — pinning isn't meaningful.
  if (import.meta.env.DEV) return DISCOVERED_URL;

  const base =
    import.meta.env.VITE_BACKEND_API_URL || "http://localhost:8081";
  // Force us-east-1 regardless of region pin. applyRegionPin's regex is a
  // no-op on already-pinned hostnames (api-dev-useast1.traci.*), so we can
  // safely apply it even if base already includes a region suffix.
  return applyRegionPin(base, "us-east-1");
}

export const BACKEND_API_URL_PRIMARY_REGION = computePrimaryRegionApiUrl();
